import { FC } from 'react';
import FaIcon from '@components/FaIcon';
import { PlanWarnings, PlanWarningsCTALabels } from '@constants/pricing';
import { setPlanUpdateErrors } from '@store/AppSlice';
import {
  getPlanValidityTitle,
  getPlanValiditySubTitle,
  updateBannerSessionAction,
  getBannerSessionValidity,
  getModalName
} from '@utils/plans';
import { RootState } from '@store/store';
import { useDispatch, useSelector } from 'react-redux';
import colors from '@theme/base/colors';
import { sendAmplitudeData } from '@utils/amplitude';
import { CLICKED_BANNER, CLICKED_UPGRADE_PLAN } from '@constants/eventNames';
import If from '@components/If';

interface PlanBannerProps {
  isUploadModal?: boolean;
  planValidityText: { type: PlanWarnings; value: string };
  setShowPlanUpgradeBanner: React.Dispatch<React.SetStateAction<boolean>>;
}

const PlanBanner: FC<PlanBannerProps> = ({
  isUploadModal,
  planValidityText,
  setShowPlanUpgradeBanner
}) => {
  const storeDispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const projectUploadLimitByPlan = useSelector(
    (state: RootState) => state?.AppData.currentPlanFeatures.projectLimit
  );
  const isShowBanner = getBannerSessionValidity();
  const planUpgradeUrl = `${process.env.REACT_APP_PLAN_UPGRADE_URL}`;

  const handleUpgradeClick = () => {
    sendAmplitudeData(CLICKED_BANNER);
    sendAmplitudeData(CLICKED_UPGRADE_PLAN, {
      current_plan: `${userDetails?.plan}`,
      is_trial_utilised: userDetails?.has_used_trial ? 'yes' : 'no'
    });

    window.location.replace(
      `${planUpgradeUrl}?userid=${userDetails?.user?.id}&plan_level=PRO`
    );
  };

  return isShowBanner ? (
    <div
      style={{
        width: '100%',
        minHeight: '50px',
        display: isUploadModal ? 'block' : 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: colors.primary.main,
        fontSize: '1rem',
        color: 'white',
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        padding: isUploadModal ? 15 : 0,
        paddingRight: isUploadModal ? 25 : 0
      }}
    >
      <>
        <If condition={planValidityText.type === PlanWarnings.LAUNCH_MESSAGE}>
          <>
            {`${planValidityText.value}`}
            <a
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                paddingLeft: '5px',
                paddingRight: '1px',
                color: 'white'
              }}
              href='https://www.producthunt.com/posts/datasquirrel-ai-2'
              target='_blank'
              rel='noreferrer'
              tabIndex={0}
              onClick={() => sendAmplitudeData(CLICKED_BANNER)}
            >
              {`${PlanWarningsCTALabels[planValidityText.type]}`}
            </a>
            <span>
              {planValidityText.type === PlanWarnings.LAUNCH_MESSAGE
                ? '! :)'
                : ' '}
            </span>
          </>
        </If>
        <If
          condition={
            planValidityText.type === PlanWarnings.FEATURE_LIMIT_EXCEEDED
          }
        >
          <span style={{ textAlign: 'center' }}>
            {`${planValidityText.value}`}
            <span
              role='link'
              tabIndex={0}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                cursor: 'pointer',
                paddingLeft: '5px',
                paddingRight: '5px',
                color: 'white'
              }}
              onClick={handleUpgradeClick}
            >
              {`${PlanWarningsCTALabels[planValidityText.type]}`}
            </span>
            <span>to continue enjoying this feature.</span>
          </span>
        </If>
        <If
          condition={
            planValidityText.type !== PlanWarnings.LAUNCH_MESSAGE &&
            planValidityText.type !== PlanWarnings.FEATURE_LIMIT_EXCEEDED
          }
        >
          {`${planValidityText.value}`}
          <span
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              paddingLeft: '5px',
              paddingRight: '3px'
            }}
            role='link'
            tabIndex={0}
            onClick={() =>
              storeDispatch(
                setPlanUpdateErrors({
                  button_text: 'Upgrade',
                  feature_name: 'Banner click',
                  modal_name: getModalName(planValidityText.type),
                  title: getPlanValidityTitle(
                    planValidityText.type,
                    (projectUploadLimitByPlan as number) >
                      userDetails?.project_count
                      ? (projectUploadLimitByPlan as number) -
                          userDetails?.project_count
                      : 0,
                    projectUploadLimitByPlan as number,
                    userDetails?.plan,
                    userDetails?.is_trial,
                    userDetails?.plan_end_date
                  ),
                  sub_title: getPlanValiditySubTitle(planValidityText.type)
                })
              )
            }
          >
            {`${PlanWarningsCTALabels[planValidityText.type]} `}
          </span>
        </If>
      </>
      <div
        style={{
          position: 'absolute',
          right: isUploadModal ? 10 : 35,
          top: isUploadModal ? 40 : 'unset'
        }}
      >
        <FaIcon
          typographyProps={{ variant: 'sm', color: 'white' }}
          icon='xmark'
          onClick={() => {
            updateBannerSessionAction();
            setShowPlanUpgradeBanner(false);
          }}
        />
      </div>
    </div>
  ) : (
    <></>
  );
};

export default PlanBanner;
